import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AvailableSlotsResponse,
  BookSlotRequest,
  BookingStatusResponse,
  RescheduleRequest,
  CancelRequest,
  Booking,
} from '../types/booking';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api/v1';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: Record<string, unknown>;
}

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'An error occurred');
  }

  const result: ApiResponse<T> = await response.json();
  return result.data;
}

/**
 * Get available slots for a booking token
 */
export function useAvailableSlots(token: string) {
  return useQuery({
    queryKey: ['booking', 'slots', token],
    queryFn: () => fetchApi<AvailableSlotsResponse>(`/booking/${token}`),
    enabled: !!token,
    retry: false,
  });
}

/**
 * Get booking status for a token
 */
export function useBookingStatus(token: string) {
  return useQuery({
    queryKey: ['booking', 'status', token],
    queryFn: () => fetchApi<BookingStatusResponse>(`/booking/${token}/status`),
    enabled: !!token,
    retry: false,
  });
}

/**
 * Book a slot
 */
export function useBookSlot(token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BookSlotRequest) =>
      fetchApi<Booking>(`/booking/${token}`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', 'slots', token] });
      queryClient.invalidateQueries({ queryKey: ['booking', 'status', token] });
    },
  });
}

/**
 * Reschedule a booking
 */
export function useReschedule(token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RescheduleRequest) =>
      fetchApi<Booking>(`/booking/${token}/reschedule`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', 'slots', token] });
      queryClient.invalidateQueries({ queryKey: ['booking', 'status', token] });
    },
  });
}

/**
 * Cancel a booking
 */
export function useCancel(token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CancelRequest) =>
      fetchApi<Booking>(`/booking/${token}/cancel`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['booking', 'slots', token] });
      queryClient.invalidateQueries({ queryKey: ['booking', 'status', token] });
    },
  });
}
